import { useCallback, useMemo } from 'react'
import { collection, query, serverTimestamp, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { reportSendFailedToast } from '../lib/commonStrings'
import { addDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { useFirestoreCollection } from './useFirestore'
import type { EventReport, EventReportCategory } from '../types'

// A user flagging something wrong on an event's listing. Clients only ever
// create — the event-reports routine records its verdict and triage happens in
// the admin queue.
export function useEventReports(eventId: string | null, userId: string | null) {
  const myReportsQuery = useMemo(
    () =>
      userId && eventId
        ? (query(
            collection(db, 'eventReports'),
            where('submittedBy', '==', userId),
            where('eventId', '==', eventId),
          ) as Query<EventReport>)
        : null,
    [userId, eventId],
  )

  const { data } = useFirestoreCollection<EventReport>(myReportsQuery)

  const pendingCategories = useMemo(
    () => new Set<EventReportCategory>(data.filter(r => r.status === 'pending').map(r => r.category)),
    [data],
  )

  const submitReport = useCallback(
    async (category: EventReportCategory, message: string, url: string) => {
      if (!userId || !eventId) return
      const trimmed = message.trim()
      await runMutation(
        reportSendFailedToast(),
        async () => {
          await addDoc(collection(db, 'eventReports'), {
            eventId,
            category,
            ...(trimmed ? { message: trimmed } : {}),
            ...(url ? { url } : {}),
            submittedBy: userId,
            createdAt: serverTimestamp(),
            status: 'pending',
          })
          track('event_report_submitted', { event_id: eventId, kind: category })
        },
      )
    },
    [userId, eventId],
  )

  return { submitReport, pendingCategories }
}
